/**
 * The route table. Romanian paths mirroring the real eShop (CLAUDE.md §Conventions),
 * and the same paths as `ROUTE_FOR` in `core/flow/flow-steps.ts` — that map is what
 * the screens navigate with, so the two must agree. A Vitest spec asserts they do,
 * rather than trusting that anyone editing one remembers the other.
 *
 * Every screen is lazily loaded. Eighteen screens with their own resolvers and forms
 * in one initial chunk would put the entry screen behind the payment screen's
 * bundle, and S-01 is the one screen whose load time a customer experiences before
 * they have decided to use the flow at all.
 *
 * Crumbs and the webview title are route `data`, read by the shell — see
 * `core/flow/page-meta.ts`.
 */
import type { Routes } from '@angular/router';
import { pageMeta } from './core/flow/page-meta';
import { devGuard } from './core/guards/dev.guard';
import { draftGuard } from './core/guards/draft.guard';
import { sessionGuard } from './core/guards/session.guard';

const CRUMB_BUSINESS = $localize`:@@crumbs.business:Business`;
const CRUMB_PLANS = $localize`:@@crumbs.plans:Abonamente`;
const CRUMB_MIGRATION = $localize`:@@crumbs.migration:Migrare cartelă`;
/** The post-order trail. The order reference is in the page, not the breadcrumbs —
 *  route `data` is static and the reference is not. */
const CRUMB_ORDERS = $localize`:@@crumbs.orders:Comenzile mele`;

/** The trail every in-flow screen sits under. */
const FLOW_CRUMBS = [CRUMB_BUSINESS, CRUMB_PLANS, CRUMB_MIGRATION];

export const routes: Routes = [
  {
    path: 'migrare',
    loadComponent: () => import('./screens/flow/entry/entry.screen').then((m) => m.EntryScreen),
    data: pageMeta({
      screen: 'S-01',
      crumbs: FLOW_CRUMBS,
      webviewTitle: $localize`:@@page.entry:Trecere la abonament`,
      // S-01 is the entry point, before the journey starts — no step bar.
      steps: false,
    }),
  },

  // S-02. POC scaffolding standing in for Orange SSO, and the screen says so. It
  // is a real route rather than a dev-only one because six of the seven personas
  // are the only way to reach an undefined branch, and hiding the picker behind
  // `?dev=1` would hide the failure states with it.
  {
    path: 'autentificare',
    loadComponent: () =>
      import('./screens/auth/persona-picker.screen').then((m) => m.PersonaPickerScreen),
    data: pageMeta({
      screen: 'S-02',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.auth:Autentificare`],
      webviewTitle: $localize`:@@page.auth:Autentificare`,
      width: 'wide',
      steps: false,
    }),
  },

  // S-03. The first guarded screen, and the first one that can be reached by
  // someone the eligibility chain says no to — `sessionGuard` resolves the block and
  // the shell renders it here, at this URL, with these breadcrumbs.
  {
    path: 'migrare/oferte',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () => import('./screens/flow/offers/offers.screen').then((m) => m.OffersScreen),
    data: pageMeta({
      screen: 'S-03',
      crumbs: FLOW_CRUMBS,
      webviewTitle: $localize`:@@page.offers:Abonamente business`,
    }),
  },

  // S-04. `draftGuard` is what makes this the first route that can bounce a customer
  // for a reason other than eligibility — arriving here with no offer chosen means
  // there is nothing for the number to be migrated onto.
  {
    path: 'migrare/numar',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () => import('./screens/flow/number/number.screen').then((m) => m.NumberScreen),
    data: pageMeta({
      screen: 'S-04',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.number:Numărul`],
      webviewTitle: $localize`:@@page.number:Numărul de migrat`,
      width: 'narrow',
    }),
  },

  // S-05. `draftGuard` requires a live OTP challenge here, not just a number —
  // otherwise arriving at this URL would have to send an SMS to make the screen
  // mean anything, and a reload would send another.
  {
    path: 'migrare/verificare',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () =>
      import('./screens/flow/verification/verification.screen').then((m) => m.VerificationScreen),
    data: pageMeta({
      screen: 'S-05',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.verification:Verificare`],
      webviewTitle: $localize`:@@page.verification:Verificare prin cod`,
      width: 'narrow',
    }),
  },

  // S-06. The first screen behind the asynchronous half of `draftGuard`'s work — it
  // is where `addToCart` is called, so the guard reads the cart from here on rather
  // than only the draft.
  {
    path: 'migrare/sim',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () => import('./screens/flow/sim/sim.screen').then((m) => m.SimScreen),
    data: pageMeta({
      screen: 'S-06',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.sim:Cartela SIM`],
      webviewTitle: $localize`:@@page.sim:Cartela SIM`,
      width: 'narrow',
    }),
  },

  // S-07. No `width`, unlike the four screens above it: the cart, the payment step
  // and the confirmation all use the working-column-plus-summary-rail layout, and the
  // 360px rail needs the 1200px shell to leave a readable measure beside it.
  {
    path: 'migrare/cos',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () => import('./screens/flow/cart/cart.screen').then((m) => m.CartScreen),
    data: pageMeta({
      screen: 'S-07',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.cart:Coș`],
      webviewTitle: $localize`:@@page.cart:Coșul tău`,
    }),
  },

  // S-08. Also no `width`, and for a second reason on top of the rail: the address
  // form is a two-column grid, and a 720px shell would collapse it on a desktop.
  {
    path: 'migrare/plata',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () =>
      import('./screens/flow/payment/payment.screen').then((m) => m.PaymentScreen),
    data: pageMeta({
      screen: 'S-08',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.payment:Plată`],
      webviewTitle: $localize`:@@page.payment:Plată și livrare`,
    }),
  },

  // S-09. The commitment point. Both terms variants live in one component and
  // `TermsVariantStore` picks between them — `?terms=b` reaches variant B.
  {
    path: 'migrare/confirmare',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () =>
      import('./screens/flow/confirm/confirm.screen').then((m) => m.ConfirmScreen),
    data: pageMeta({
      screen: 'S-09',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.confirm:Confirmare`],
      webviewTitle: $localize`:@@page.confirm:Verifică și confirmă`,
    }),
  },

  // S-11. Journey B's first screen. It sits after S-09 in this list rather than
  // between S-03 and S-04 because route order in Angular is matching order, not
  // journey order — the journey order lives in `flow-steps.ts`, which is what the
  // stepper and `draftGuard` read.
  //
  // No `width`: the catalogue is a three-across grid and the 720px narrow shell
  // would drop it to two columns on a desktop.
  {
    path: 'migrare/telefoane',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () =>
      import('./screens/flow/devices/devices.screen').then((m) => m.DevicesScreen),
    data: pageMeta({
      screen: 'S-11',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.devices:Telefoane`],
      webviewTitle: $localize`:@@page.devices:Alege telefonul`,
    }),
  },

  // S-12. The only parameterised flow route. `:id` arrives as a component input
  // through `withComponentInputBinding()`, and the screen reconciles the draft to it
  // rather than trusting the draft — the URL is what the customer is looking at.
  //
  // `draftGuard` lets this through on a pending device and redirects to S-11
  // otherwise, so a bookmarked handset URL still works: S-11's tile sets the draft
  // before navigating, and a cold arrival lands back on the catalogue.
  {
    path: 'migrare/telefon/:id',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () =>
      import('./screens/flow/device-detail/device-detail.screen').then((m) => m.DeviceDetailScreen),
    data: pageMeta({
      screen: 'S-12',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.deviceDetail:Telefon`],
      webviewTitle: $localize`:@@page.deviceDetail:Telefonul ales`,
    }),
  },

  // S-13. Narrow, like S-04 to S-06: one figure has to dominate and a 1200px measure
  // would put the monthly total and the plan cards on opposite sides of the screen.
  {
    path: 'migrare/rate',
    canActivate: [sessionGuard, draftGuard],
    loadComponent: () =>
      import('./screens/flow/instalments/instalments.screen').then((m) => m.InstalmentsScreen),
    data: pageMeta({
      screen: 'S-13',
      crumbs: [...FLOW_CRUMBS, $localize`:@@crumbs.instalments:Rate`],
      webviewTitle: $localize`:@@page.instalments:Plata în rate`,
      width: 'narrow',
    }),
  },

  // S-14 · S-15 · S-16. One route for the placed order, switching on `OrderStatus`.
  // S-10 and S-17 land here too once they are built — the order is one page whose
  // content is decided by its status, not five pages a router picks between.
  //
  // ### `sessionGuard` only, deliberately
  //
  // `draftGuard` must not be attached. It reads `meta.screen` and walks the flow
  // backwards looking for the first unsatisfied step — and S-09 clears the draft
  // before navigating here, so on a freshly placed order it would find every step
  // unsatisfied and redirect the customer off the order they just paid for. There is
  // no draft to guard at this point; there is an order, and the screen reads it.
  //
  // No `screen` in the meta either, for a related reason: the identifier would have
  // to be one of five, the stepper is over, and `previousScreen` has nothing to
  // answer. `steps: false` says the same thing to the shell explicitly.
  {
    path: 'migrare/comanda/:reference',
    canActivate: [sessionGuard],
    loadComponent: () =>
      import('./screens/orders/order-status.screen').then((m) => m.OrderStatusScreen),
    data: pageMeta({
      crumbs: [CRUMB_BUSINESS, CRUMB_ORDERS],
      webviewTitle: $localize`:@@page.orderStatus:Starea comenzii`,
      width: 'narrow',
      steps: false,
    }),
  },

  // S-18. Not part of the flow: reachable from site navigation at any time, so no
  // `draftGuard`, no `screen` and no stepper. The comment on the order route above
  // applies here for the same reasons.
  {
    path: 'comenzile-mele',
    canActivate: [sessionGuard],
    loadComponent: () =>
      import('./screens/orders/orders-list.screen').then((m) => m.OrdersListScreen),
    data: pageMeta({
      crumbs: [CRUMB_BUSINESS, CRUMB_ORDERS],
      webviewTitle: $localize`:@@page.orders:Comenzile mele`,
      steps: false,
    }),
  },

  // S-19. No `width`: this is the only screen in the application with a rail, and
  // `narrow` (680px) cannot hold two columns. `.cols` handles the collapse itself.
  //
  // The reference is not in the crumbs. `pageMeta` takes static strings and the
  // reference is only known once the route resolves; a crumb trail that stops at
  // "Comenzile mele" is accurate, and the reference is the screen's own first line.
  {
    path: 'comenzile-mele/:reference',
    canActivate: [sessionGuard],
    loadComponent: () =>
      import('./screens/orders/order-detail.screen').then((m) => m.OrderDetailScreen),
    data: pageMeta({
      crumbs: [CRUMB_BUSINESS, CRUMB_ORDERS],
      webviewTitle: $localize`:@@page.orderDetail:Detaliile comenzii`,
      steps: false,
    }),
  },

  // The two dev pages. English paths, deliberately: every product route is Romanian and
  // mirrors the real eShop, so `/dev` reads as not-a-product-route at a glance. Behind
  // `devGuard`, which redirects to S-01 when dev mode is off — the S-20 catalogue is
  // labelled "NU ESTE UN SINGUR ECRAN" and must not be stumbled into.
  //
  // Above the `**` catch-all, and lazily loaded: the catalogue pulls in the registry, the
  // trigger routes and `SwitchBoard`, none of which a customer's bundle should contain.
  {
    path: 'dev',
    canActivate: [devGuard],
    loadComponent: () => import('./dev/pages/dev-index.screen').then((m) => m.DevIndexScreen),
  },
  {
    path: 'dev/blocked',
    canActivate: [devGuard],
    loadComponent: () =>
      import('./dev/pages/blocked-catalogue.screen').then((m) => m.BlockedCatalogueScreen),
  },

  { path: '', pathMatch: 'full', redirectTo: 'migrare' },

  // An unknown path redirects rather than showing a not-found screen: there is no
  // 404 design in any source, and inventing one would be inventing a screen.
  { path: '**', redirectTo: 'migrare' },
];
